import { FetchResult } from '@apollo/client';

import { redirectToUrl } from 'service/browser';
import { DataRow, EventAction } from 'plugins/types';
import * as K from 'common/constants';
import { UrlParams } from 'common/types';

import { createActionPayload, getActionUrl } from './utils';

/**
 * Apply action by API call to mgmt console
 *
 * @param action                          data configuration to apply
 * @param rowData                         data of event to apply the action on
 * @param s1PerformGenericAgentAction     mutation to perform the action
 */
export const executeAction = async (
  action: EventAction,
  rowData: DataRow,
  s1PerformGenericAgentAction: (options: any) => Promise<FetchResult<any>>,
) => {
  const actionPath = `/web/api/v2.1/${action.actionPath}/${action.actionId}`;

  const params = action.getParams ? action.getParams(rowData) : '';

  const mgmtUrl = `https://${rowData['mgmt.url']}${params}`;

  const variables: any = {
    console: mgmtUrl,
    actionPath: actionPath,
  };

  const payloadData = createActionPayload(action, rowData);

  if (payloadData) {
    variables.data = JSON.stringify(payloadData.data);
    variables.filter = JSON.stringify(payloadData.filter);
  }

  const payload = { variables };

  // The number of affected endpoints returned by the API
  return s1PerformGenericAgentAction(payload).then(({ data }) => data.s1PerformGenericAgentAction.data.affected);
};

/**
 * Handle action by redirecting to mgmt console
 * @param action                    data configuration to apply
 * @param rowData                   data of event to apply the action on
 * @param s1CreateConsoleAction     mutation to perform the action
 */
export const handleRedirectAction = async (
  action: EventAction,
  rowData: DataRow,
  s1CreateConsoleAction: (options: any) => Promise<FetchResult<any>>,
) => {
  const mgmtUrl = rowData['mgmt.url'] ? `https://${rowData['mgmt.url']}` : `${rowData['origin.mgmt.url'] || ''}`;

  if (action.actionId) {
    const actionData = createActionPayload(action, rowData);

    const payload = {
      variables: {
        console: mgmtUrl,
        context: JSON.stringify({
          action: action.actionName || action.actionId,
          data: actionData.filter || actionData,
        }),
      },
    };

    return s1CreateConsoleAction(payload).then((result) => {
      const actionId = result?.data?.s1CreateConsoleAction;
      const url = getActionUrl(actionId, mgmtUrl, action.redirectPath as string);
      redirectToUrl(url);
    });
  } else {
    const params = action.getParams ? action.getParams(rowData) : '';
    const url = `${mgmtUrl}/${action.redirectPath}${params}`;
    redirectToUrl(url);
  }
};

export const handleChangeSearchAction = (
  action: EventAction,
  values: DataRow,
  setSearchParams: (params: { [K.URL_PARAM.FILTER]: string }) => void,
) => {
  const callback = (queryParams: { [key: string]: any }) => {
    setSearchParams(queryParams as UrlParams);
  };

  action?.actionHandler?.(values, callback);
};
